/**
 * Light and dark mode — the preference, and how it reaches the document.
 *
 * This lives in `shared` rather than in `packages/ui` because the landing page
 * needs it too and does not depend on `ui`. Nothing here runs at import time, so
 * the API can carry it in its module graph without touching `window`.
 *
 * ## Three states, not two
 *
 * "System" is a real preference, distinct from whichever of light or dark the
 * system currently reports. A merchant who has never chosen follows their phone
 * from morning to evening; one who has chosen light keeps light at 9pm. Storing
 * only the resolved value would collapse those two people into the same user and
 * quietly override a deliberate choice — the same mistake the locale bootstrap
 * in `apps/app/src/main.tsx` avoids for the same reason.
 */
export const THEME_PREFERENCES = ['system', 'light', 'dark'];
export const THEME_STORAGE_KEY = 'signapass.theme';
/**
 * The `<meta name="theme-color">` value per theme — the browser chrome around
 * the page. These are `--canvas` in each theme; a mismatch shows as a stripe
 * above the counter's header on Android.
 */
export const THEME_COLOR = {
    light: '#EFEDE8',
    dark: '#0F0D20',
};
export function isThemePreference(value) {
    return (typeof value === 'string' && THEME_PREFERENCES.includes(value));
}
/** What the OS is asking for right now. Defaults to light off-browser. */
export function systemTheme() {
    if (typeof window === 'undefined' || !window.matchMedia)
        return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}
export function resolveTheme(preference, system = systemTheme()) {
    return preference === 'system' ? system : preference;
}
/**
 * The stored preference, or `system` when there is none.
 *
 * Storage can throw — Safari in private mode, a locked-down embedded WebView —
 * and a theme is never worth failing a page load over.
 */
export function readThemePreference() {
    try {
        const stored = globalThis.localStorage?.getItem(THEME_STORAGE_KEY);
        return isThemePreference(stored) ? stored : 'system';
    }
    catch {
        return 'system';
    }
}
export function writeThemePreference(preference) {
    try {
        globalThis.localStorage?.setItem(THEME_STORAGE_KEY, preference);
    }
    catch {
        // Preference is lost on reload. The page still renders in the chosen theme.
    }
}
/**
 * Put a resolved theme on the document.
 *
 * The class on `<html>` is what `@custom-variant dark` in `tokens.css` matches.
 * The `theme-color` meta is updated alongside it rather than being left at the
 * build-time value, so the browser chrome follows a runtime switch.
 */
export function applyResolvedTheme(theme) {
    if (typeof document === 'undefined')
        return;
    document.documentElement.classList.toggle('dark', theme === 'dark');
    document
        .querySelector('meta[name="theme-color"]')
        ?.setAttribute('content', THEME_COLOR[theme]);
}
/**
 * The no-flash bootstrap, as source, for inlining in `<head>`.
 *
 * It has to run before first paint: the stylesheet's default is light, so a dark
 * user would otherwise get a white page for as long as it takes the bundle to
 * parse — on the cheap Android the counter is designed for, long enough to be
 * the first thing they notice. Anything that can throw is inside the try, since
 * a theme is not worth a blank page.
 *
 * `apps/app/index.html` inlines this verbatim (it is a static file that cannot
 * import); `tokens-parity.test.ts` fails if the copy drifts.
 */
export const THEME_BOOTSTRAP_SCRIPT = `try{var t=localStorage.getItem('${THEME_STORAGE_KEY}');var d=t==='dark'||((!t||t==='system')&&matchMedia('(prefers-color-scheme: dark)').matches);document.documentElement.classList.toggle('dark',d);var m=document.querySelector('meta[name="theme-color"]');if(m)m.content=d?'${THEME_COLOR.dark}':'${THEME_COLOR.light}';}catch(e){}`;
//# sourceMappingURL=theme.js.map